"use client";

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

export type SignaturePadHandle = {
  /** PNG dataURL, or '' when nothing has been drawn. */
  toDataURL: () => string;
  /** True when the user has not drawn any stroke yet. */
  isEmpty: () => boolean;
  /** Clear the canvas and reset the empty state. */
  clear: () => void;
};

type Props = {
  height?: number;
  onChange?: (empty: boolean) => void;
};

type Pt = { x: number; y: number };

const INK = "#020617"; // slate-950
const LINE_WIDTH = 2.5;
const BG = "#ffffff";

/**
 * Handwritten signature canvas.
 * - 2x DPR scaling for crisp strokes on retina screens.
 * - Pointer events with touch-action:none so drawing works on mobile.
 * - Strokes kept in memory so a resize/DPR change does not wipe the drawing.
 */
const SignaturePad = forwardRef<SignaturePadHandle, Props>(function SignaturePad(
  { height = 220, onChange }: Props,
  ref
) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const strokesRef = useRef<Pt[][]>([]);
  const drawingRef = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.save();
    ctx.fillStyle = BG;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = INK;
    ctx.lineWidth = LINE_WIDTH;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    for (const stroke of strokesRef.current) {
      if (stroke.length === 0) continue;
      ctx.beginPath();
      // DPR transform is applied via setTransform at setup; draw in CSS px.
      ctx.moveTo(stroke[0].x, stroke[0].y);
      for (let i = 1; i < stroke.length; i++) ctx.lineTo(stroke[i].x, stroke[i].y);
      // Single tap = dot.
      if (stroke.length === 1) ctx.lineTo(stroke[0].x + 0.1, stroke[0].y + 0.1);
      ctx.stroke();
    }
    ctx.restore();
  }, []);

  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    const w = wrap.clientWidth;
    const h = height;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    canvas.width = Math.max(1, Math.round(w * dpr));
    canvas.height = Math.max(1, Math.round(h * dpr));
    const ctx = canvas.getContext("2d");
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    redraw();
  }, [height, redraw]);

  useEffect(() => {
    setupCanvas();
    const onResize = () => setupCanvas();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [setupCanvas]);

  const pos = (e: React.PointerEvent<HTMLCanvasElement>): Pt => {
    const canvas = canvasRef.current!;
    const r = canvas.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const notify = () => {
    onChangeRef.current?.(strokesRef.current.length === 0);
  };

  useImperativeHandle(
    ref,
    () => ({
      toDataURL: () => {
        if (strokesRef.current.length === 0) return "";
        return canvasRef.current?.toDataURL("image/png") ?? "";
      },
      isEmpty: () => strokesRef.current.length === 0,
      clear: () => {
        strokesRef.current = [];
        drawingRef.current = false;
        redraw();
        notify();
      },
    }),
    [redraw]
  );

  return (
    <div>
      <div
        ref={wrapRef}
        className="w-full overflow-hidden rounded-xl border border-slate-300 bg-white"
      >
        <canvas
          ref={canvasRef}
          className="block touch-none cursor-crosshair"
          onPointerDown={(e) => {
            e.preventDefault();
            (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
            drawingRef.current = true;
            strokesRef.current.push([pos(e)]);
            redraw();
          }}
          onPointerMove={(e) => {
            if (!drawingRef.current) return;
            e.preventDefault();
            const cur = strokesRef.current[strokesRef.current.length - 1];
            cur.push(pos(e));
            redraw();
          }}
          onPointerUp={() => {
            if (!drawingRef.current) return;
            drawingRef.current = false;
            // Drop accidental empty strokes.
            strokesRef.current = strokesRef.current.filter((s) => s.length > 0);
            redraw();
            notify();
          }}
          onPointerCancel={() => {
            drawingRef.current = false;
            strokesRef.current = strokesRef.current.filter((s) => s.length > 0);
            redraw();
            notify();
          }}
          aria-label="Zone de signature manuscrite"
        />
      </div>
      <div className="mt-2 flex justify-end">
        <button
          type="button"
          onClick={() => {
            strokesRef.current = [];
            drawingRef.current = false;
            redraw();
            notify();
          }}
          className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 active:bg-slate-100"
        >
          Effacer
        </button>
      </div>
    </div>
  );
});

export default SignaturePad;
